// client/src/components/marketer/MarketerPointRefund.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePoints } from '../../contexts/PointContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function MarketerPointRefund() {
  const { refreshPoints } = usePoints();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestedPoints, setRequestedPoints] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  // 환불 상태 조회
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketer/mybiz/points/refund/status`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setStatus(data);
      } else {
        setMessage('상태를 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('fetchStatus error:', error);
      setMessage('상태를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 포인트도 갱신
  useEffect(() => {
    refreshPoints(); 
    fetchStatus();   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 예상 환불 금액 계산 (110%)
  const estimatedRefund = useMemo(() => {
    const points = Number(requestedPoints) || 0;
    return Math.round(points * 1.1);
  }, [requestedPoints]);

  // 유효성 검사
  const isValid = useMemo(() => {
    const points = Number(requestedPoints) || 0;
    if (points < 10000) return false;
    if (points > (status?.availablePoints || 0)) return false;
    if (points % 1 !== 0) return false;
    return true;
  }, [requestedPoints, status]);

  const handleSubmit = async () => {
    if (!isValid) return;
    
    setSubmitting(true);
    setMessage('');
    
    try {
      const res = await fetch(`${API_URL}/api/marketer/mybiz/points/refund/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          requestedPoints: Number(requestedPoints)
        })
      });
      
      const data = await res.json();
      
      if (data.ok) {
        setMessage('✅ 환불 신청이 완료되었습니다.');
        setRequestedPoints('');
        setShowConfirmModal(false);
        
        await refreshPoints();
        await fetchStatus();
      } else {
        setMessage(`❌ ${data.message || '환불 신청에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('handleSubmit error:', error);
      setMessage('❌ 환불 신청 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-card">
        <div className="mb-page-header">
          <h2 className="mb-page-title">포인트 환불하기</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--mb-text-sub)' }}>불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="mb-card mb-card--full-height">
      <div className="mb-page-header">
        <h2 className="mb-page-title">포인트 환불하기</h2>
        <p className="mb-page-description">잔여 포인트를 환불 신청할 수 있습니다.</p>
      </div>

      <div className="summary" style={{ boxShadow: 'inset 0 0 0 1px #eceef2' }}>
        <div className="summary__item">
          <span className="summary__k">환불 가능 포인트</span>
          <span className="summary__v is-blue">{(status?.availablePoints || 0).toLocaleString()}<small>P</small></span>
        </div>
        {status?.pendingRefundPoints > 0 && (
          <div className="summary__item">
            <span className="summary__k">환불 처리 중</span>
            <span className="summary__v is-amber">{status.pendingRefundPoints.toLocaleString()}<small>P</small></span>
          </div>
        )}
      </div>

      {/* 진행 중인 환불 신청이 있는 경우 */}
      {status?.hasPendingRequest && (
        <div style={{
          background: '#fff', borderRadius: 16, padding: '20px 22px', marginBottom: 24,
          boxShadow: 'inset 0 0 0 1.5px #eceef2',
        }}>
          <span className="mb-badge mb-badge-amber mb-badge--dot">환불 신청 처리 중</span>
          <p style={{ margin: '12px 0 0', fontSize: 13.5, fontWeight: 600, color: '#3b4658', lineHeight: 1.6 }}>
            현재 환불 신청이 처리 중입니다. 관리자가 확인 후 처리될 예정입니다.
          </p>
          {status.pendingRequest && (
            <dl style={{ margin: '12px 0 0', background: '#f6f8fb', borderRadius: 14, padding: '6px 16px' }}>
              <div className="kv"><dt>신청 포인트</dt><dd>{status.pendingRequest.requestedPoints.toLocaleString()}P</dd></div>
              <div className="kv"><dt>예상 환불 금액</dt><dd style={{ color: '#2f6bff' }}>{status.pendingRequest.refundAmount.toLocaleString()}원</dd></div>
              <div className="kv"><dt>신청일</dt><dd>{new Date(status.pendingRequest.requestedAt).toLocaleDateString()}</dd></div>
            </dl>
          )}
        </div>
      )}

      {/* 환불 신청 폼 */}
      {!status?.hasPendingRequest && (
        <div className="mb-grid-2" style={{ alignItems: 'stretch' }}>
          {/* 왼쪽: 환불 안내 */}
          <div style={{ border: '1px solid #eceef2', borderRadius: 16, padding: 20 }}>
            <h3 style={{ fontSize: 15.5, fontWeight: 800, margin: '0 0 10px', color: '#0b1324' }}>환불 안내</h3>
            <div className="checkline">
              <i>✓</i>
              <div>
                <b>최소 환불 포인트</b>
                <span>10,000P 이상부터 신청할 수 있습니다</span>
              </div>
            </div>
            <div className="checkline">
              <i>✓</i>
              <div>
                <b>환불 금액 계산</b>
                <span>포인트 × 110% (부가세 포함)</span>
              </div>
            </div>
            <div className="checkline">
              <i>✓</i>
              <div>
                <b>처리 시간</b>
                <span>영업일 기준 1~3일 소요됩니다</span>
              </div>
            </div>
            <div className="note note--red">신청 즉시 포인트가 차감됩니다. 거절 시 포인트는 복구됩니다.</div>
          </div>

          {/* 오른쪽: 신청 폼 */}
          <div style={{ border: '1px solid #eceef2', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column' }}>
            <div className="mb-info-field">
              <label className="mb-info-label">환불 희망 포인트</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input
                  type="number"
                  value={requestedPoints}
                  onChange={(e) => setRequestedPoints(e.target.value)}
                  placeholder="10,000"
                  min="10000"
                  max={status?.availablePoints || 0}
                  step="1000"
                  className="mb-form-control"
                  disabled={submitting}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, fontSize: 15, color: '#6b7686', flexShrink: 0 }}>P</span>
              </div>
              <p className="mb-info-hint">10,000P 이상, 보유 포인트 이내로 입력하세요.</p>
            </div>

            {requestedPoints && (
              <div style={{
                background: isValid ? '#eef3ff' : '#ffefef', borderRadius: 14, padding: '14px 16px',
                marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: isValid ? '#1f4fd6' : '#d13b3b' }}>예상 환불 금액</span>
                <b style={{ fontSize: 19, color: isValid ? '#1f4fd6' : '#d13b3b', fontVariantNumeric: 'tabular-nums' }}>
                  {estimatedRefund.toLocaleString()}원
                </b>
              </div>
            )}

            {!isValid && requestedPoints && (
              <div className="mb-info-hint" style={{ color: '#e5484d', fontWeight: 600, marginTop: -4, marginBottom: 14 }}>
                {Number(requestedPoints) < 10000 && '최소 10,000P 이상 입력해주세요.'}
                {Number(requestedPoints) > (status?.availablePoints || 0) && '보유 포인트를 초과할 수 없습니다.'}
                {Number(requestedPoints) % 1 !== 0 && '정수만 입력 가능합니다.'}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowConfirmModal(true)}
              disabled={!isValid || submitting}
              className="mb-btn mb-btn-danger-solid mb-btn-full mb-btn-lg"
              style={{ marginTop: 'auto' }}
            >
              환불 신청하기
            </button>

            {message && (
              <div className={`note ${message.startsWith('✅') ? 'note--mint' : 'note--red'}`}>
                {message.replace(/^[✅❌]\s*/, '')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 확인 모달 */}
      {showConfirmModal && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 400, margin: 0 }}>
            <div className="mbm-head">
              <h3 className="mb-page-title mbm-title">환불 신청 확인</h3>
              <button
                type="button"
                className="mbm-close"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                aria-label="닫기"
              >
                ✕
              </button>
            </div>
            <dl style={{ margin: '16px 0 0' }}>
              <div className="kv">
                <dt>신청 포인트</dt>
                <dd>{Number(requestedPoints).toLocaleString()} pt</dd>
              </div>
              <div className="kv">
                <dt>예상 환불 금액</dt>
                <dd>{estimatedRefund.toLocaleString()} 원</dd>
              </div>
            </dl>
            <div className="note note--red">
              ⚠️ 환불 신청 시 즉시 포인트가 차감됩니다.<br/>
              관리자 확인 후 처리되며, 거절 시 포인트가 복구됩니다.
            </div>
            <div className="mbm-foot">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                className="mb-btn mb-btn-secondary"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="mb-btn mb-btn-danger-solid"
              >
                {submitting ? '처리 중...' : '신청하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketerPointRefund;
